import { SelectionModel } from '@angular/cdk/collections';
import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { DocumentProcessingStatus, FolderWithPaginatedDocuments } from '@api/types';

import { RavIcon, RavIconCircled, RavIconCircledOutlined, RavIconOutlined } from '@shared/icon/icon';
import { FolderNode } from '@shared/layout/sidebar-menu/folder-menu/folder-menu.datasource';
import { DragDropRegion, DragDropService } from '@shared/services/drag-drop.service';
import { EchoService } from '@shared/services/echo.service';
import { EditorService } from '@shared/services/editor.service';
import { FolderReferenceService } from '@shared/services/folder-reference.service';
import { FormatService } from '@shared/services/format.service';
import { ImportStatusService } from '@shared/services/import-status.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { MobileService } from '@shared/services/mobile.service';
import { ScopeService } from '@shared/services/scope.service';
import { DragImportObject, ImportType } from '@shared/types/import';

import { DeviceDetectorService } from 'ngx-device-detector';

import { BehaviorSubject, merge, Observable, Subject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { TableConfig, TableSearch, TableSort } from '../types/table';
import { AccountE2eEncryption } from './../../api/types/account';
import { EncryptionHelperService } from './../services/encryption-helper.service';
import { ActionEnabledRule, ActionTrigger, TableAction, TableActions } from './actions/action';
import { MobileTableComponent } from './mobile-table/mobile-table.component';
import { TableDataSource } from './table.datasource';

/**
 * Displays paginated data in a table format.
 */
@Component({
  selector: 'rav-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TableComponent implements OnInit, AfterViewInit, OnDestroy {
  // TODO replace return type with generic PaginationGetParams
  get paginationParams(): Params {
    const params: Params = {
      page: this.paginator.pageIndex + 1,
      per_page: this.paginator.pageSize
    };
    if (this.sort.direction === 'asc') params.asc = this.sort.direction;
    if (this.sort.active) params.order_by = this.getSortParam(this.sort.active);
    this.activeSearches.forEach((search) => {
      params[search.field] = search.value;
    });
    return params;
  }

  /**
   * Gets current url params.
   *
   * @returns a Params object containing key value pairs for current url parameters.
   */
  get urlParams(): Params {
    return this.activatedRoute.snapshot.queryParams;
  }

  /**
   * Sets url params. Note that whereas it won't reload the page, it will push a
   * new entry to the browser's history.
   *
   * @param params object containing key value pairs.
   */
  set urlParams(params: Params) {
    const urlTree = this.router.createUrlTree([], {
      queryParams: params,
      relativeTo: this.activatedRoute,
      skipLocationChange: true,
      replaceUrl: true
    });
    this.location.go(urlTree.toString());
  }

  /**
   * Determines what columns to display by the key names.
   *
   * @returns the select key and the first column key if is mobile and all column keys if not
   */
  get columnsToDisplay(): string[] {
    const columns = this.config.columns.map((column) => column.key);
    if (this.mobile.isMobileDisplay() && this.config.actions) columns.push('mobileAction');
    if (this.actionsRequireSelection(this.config.actions)) {
      columns.unshift('select');
    }
    return columns;
  }

  @Input() headerText: string;
  @Input() dataSource: TableDataSource;
  @Input() config: TableConfig;
  @Input() contextMenuConfig: TableConfig;
  @Output() actionTriggered = new EventEmitter<ActionTrigger<any>>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('mobileTable', { static: false }) mobileTable: MobileTableComponent;
  @ViewChild('selectAll', { static: false }) selectAll: MatCheckbox;
  @ViewChild('rightClickMenuTrigger', { static: true }) rightClickMenuTrigger: MatMenuTrigger;

  dataRows: any[];
  processingDictionary: any = {};
  totalItems = 0;
  activeSearches: TableSearch[] = [];
  paginatorLength$: Observable<number>;
  selection: SelectionModel<any>;
  selectionChangesSubject: Subject<SelectionModel<any>> = new Subject<SelectionModel<any>>();
  sortChangesSubject: Subject<MatSort> = new Subject<MatSort>();
  lastRowSelectedIndex: number;
  TableActions = TableActions;
  ActionEnabledRule = ActionEnabledRule;
  DragDropRegion = DragDropRegion;
  RavIconCircled = RavIconCircled;
  RavIconOutlined = RavIconOutlined;
  RavIcon = RavIcon;
  RavIconCircledOutlined = RavIconCircledOutlined;
  rightClickMenuPosition = { x: '0px', y: '0px' };
  lastRowRightClicked: any;
  subscriptions: Subscription[] = [];
  isDraggingOver = false;
  dragZoneRowTitle: string;
  dragZoneRowId: string;
  currentRootFolder: string;
  urlPath: string[];
  canDragDropFiles = false;
  isRowEditorOpen = false;
  tableControlUsageCount = 0;

  // used in e2e encryption status check
  e2eEncryptionStatus: AccountE2eEncryption;

  private paginatorLength = new BehaviorSubject<number>(0);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
    private editorService: EditorService,
    private echo: EchoService,
    private snackbar: MatSnackBar,
    private folderReferenceService: FolderReferenceService,
    private deviceService: DeviceDetectorService,
    private importStatusService: ImportStatusService,
    private location: Location,
    private e2eEncryptionHelper: EncryptionHelperService,
    public dragDrop: DragDropService,
    public format: FormatService,
    public localStorage: LocalStorageService,
    public mobile: MobileService,
    public scope: ScopeService
  ) {
    this.paginatorLength$ = this.paginatorLength.asObservable();
    this.e2eEncryptionHelper.accountEncryptionStatus.subscribe((status) => {
      this.e2eEncryptionStatus = status;
      if (this.dataRows) {
        this.e2eEncryptionStatusCheck(status);
      }
    });
  }

  @HostListener('body:dragover', ['$event'])
  onBodyDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
  }
  @HostListener('body:drop', ['$event'])
  onBodyDrop(event) {
    event.preventDefault();
  }

  /**
   * Listens for window resize events and adjusts
   * layout content padding as needed.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.adjustLayoutContentPadding();
  }

  /**
   * Listens for browser back button click and reloads table if
   * necessary.
   *
   * If you navigate directly to a page with parameters (for
   * example: "/inbox?page=2&per_page=5), then use the table
   * controls only once, and then click browser back button,
   * queryParams subscription won't be triggered, so table
   * would not be updated. This method reloads the table in
   * those cases.
   *
   */
  @HostListener('window:popstate', ['$event'])
  onPopState() {
    if (!this.config.preventUrlParamHandling) {
      const firstTableLoadParams = this.localStorage.firstTableLoadParams;
      if (firstTableLoadParams && this.tableControlUsageCount === 1) {
        this.loadTable({ customParams: firstTableLoadParams });
      }
      this.tableControlUsageCount--;
    }
  }

  /**
   * Reads the parameters from the url and assigns them to the properties of
   * paginator and sort objects.
   */
  loadQueryParamsIntoTable() {
    const urlParams = this.urlParams;
    if (!isNaN(urlParams.page) && +urlParams.page > 0) this.paginator.pageIndex = +urlParams.page - 1;
    if (!isNaN(urlParams.per_page) && +urlParams.per_page > 0) {
      this.paginator.pageSize = +urlParams.per_page > 100 ? 100 : +urlParams.per_page;
    } else {
      this.paginator.pageSize = this.localStorage.tablePerPage;
    }
    this.sort.direction = urlParams.asc === 'asc' ? urlParams.asc : undefined;
    this.sort.active = urlParams.order_by ? urlParams.order_by : undefined;
  }

  typeOf(val) {
    return typeof val;
  }

  /**
   * Takes the column to be used to sort the table by and checks if the attribute
   * to pass as a parameter to the api should be the column attribute name or a different one specified in the config
   *
   * @param colName the name of the column to sort the table by.
   * @returns the attribute name to use a sorting parameter.
   */
  getSortParam(colName: string): string {
    const column = this.config.columns.find((col) => col.key === colName);
    if (column) return column.sortBy ? column.sortBy : colName;
    return colName;
  }

  ngOnInit() {
    const deviceInfo = this.deviceService.getDeviceInfo();
    this.urlPath = this.router.url.includes('?')
      ? this.router.url.substring(0, this.router.url.indexOf('?')).split('/')
      : this.router.url.split('/');
    this.currentRootFolder = this.urlPath[1];
    this.canDragDropFiles = !(
      this.deviceService.isMobile() ||
      this.deviceService.isTablet() ||
      deviceInfo.browser === 'IE' ||
      (this.currentRootFolder !== 'folders' && this.currentRootFolder !== 'inbox')
    );
    this.selection = new SelectionModel<any>(true, []);
    this.processingDictionary = {};

    this.subscriptions.push(
      this.dataSource.paginatedGet$.subscribe((paginatedGet) => {
        this.totalItems = paginatedGet.total;
        this.paginatorLength.next(paginatedGet.total);
        this.dataRows = paginatedGet.data;
        this.deselectAllDataRows();

        // If current page property exists the paginator is updated in the table
        // this is useful when the last search is loaded
        if (paginatedGet.current_page) this.paginator.pageIndex = paginatedGet.current_page - 1;

        this.openSocketOnPendingFiles(this.dataRows);

        this.adjustLayoutContentPadding();

        this.e2eEncryptionStatusCheck(this.e2eEncryptionStatus);
      }),

      // If rename editor is open then this row does not allow to drag and drop, otherwise it allows to drag and drop
      this.editorService.editorState$.subscribe(({ id, open }) => {
        // if editor's id does not exist or not open, which should allow to can drag and drop, so we set canDragRows to be true
        if (!id || !open) {
          this.isRowEditorOpen = false;
        }
        if (open) {
          this.isRowEditorOpen = true;
        }
      }),

      this.activatedRoute.queryParams.subscribe(() => {
        if (!this.config.preventUrlParamHandling) {
          this.loadQueryParamsIntoTable();
          if (this.localStorage.previousUrl === this.router.url.split('?')[0]) this.loadTable();
        }
      })
    );

    if (!this.contextMenuConfig) {
      this.contextMenuConfig = this.config;
    }

    this.subscriptions.push(
      this.importStatusService.currentTablePageRefreshAnnouncedSource$.subscribe(() => {
        this.loadTable();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    for (const documentId in this.processingDictionary) {
      if (documentId) {
        this.echo.leaveChannel(`documents.${documentId}`);
      }
    }
    this.processingDictionary = {};
  }

  /**
   * Open sockets to listen for pending data.
   *
   * @param dataRows table row
   */
  openSocketOnPendingFiles(dataRows: any[]) {
    for (let i = 0; i < dataRows.length; i++) {
      const data = dataRows[i];
      if (data.processing_status) {
        if (data.processing_status === DocumentProcessingStatus.Pending) {
          this.processingDictionary[data.id] = true;

          this.echo.getPrivateChannel(`documents.${data.id}`).listen('DocumentProcessed', (event) => {
            this.echo.leaveChannel(`documents.${data.id}`);
            dataRows[i].processing_status = event.processing_status;
            this.processingDictionary[data.id] = false;

            if (event.processing_status === DocumentProcessingStatus.Error) {
              this.snackbar.open(`File ${data.title} encountered an error and failed to import`, '', {
                duration: 5000,
                panelClass: ['alert-bg', 'bold', 'light-color']
              });
            } else {
              this.snackbar.open(`File ${data.title} has been processed successfully.`, '', {
                duration: 5000,
                panelClass: ['success-bg', 'bold', 'light-color', 'word-break']
              });
            }
          });
        } else this.processingDictionary[data.id] = false;
      }
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.subscriptions.push(
        this.paginator.page.subscribe((data) => {
          this.localStorage.tablePerPage = data.pageSize;
        })
      );
      if (!this.config.preventUrlParamHandling) {
        if (JSON.stringify(this.urlParams) !== '{}') this.loadQueryParamsIntoTable();
        this.localStorage.firstTableLoadParams = this.paginationParams;
      }
      this.loadTable();
      this.subscriptions.push(
        this.dataSource.reloadData.subscribe((data) => {
          let options: any = {};
          if (data && data.pageIndex >= 0) this.paginator.pageIndex = data.pageIndex;
          if (data) options = { isFromSubmitButton: data.isFromSubmitButton };
          this.loadTable(options);
        })
      );
      this.sortChangesSubject.next(this.sort);
      this.selectionChangesSubject.next(this.selection);
      this.subscriptions.push(
        this.sort.sortChange.subscribe(() => {
          this.paginator.pageIndex = 0;
          this.sortChangesSubject.next(this.sort);
        }),
        this.selection.changed.subscribe(() => {
          this.selectionChangesSubject.next(this.selection);
        })
      );
      merge(this.sort.sortChange, this.paginator.page)
        // reload table after sort or page change
        .pipe(
          tap(() => {
            if (!this.config.preventUrlParamHandling) {
              this.tableControlUsageCount++;
              this.urlParams = this.paginationParams;
            }
            this.loadTable();
          })
        )
        .subscribe();
    });
  }

  /**
   * Determines if the actions require a selection.
   *
   * Filters the actions, removing an action with `enableRule.Always` enabled.
   * All others need a selection and will not be filtered out. If there are any
   * selection enable rules the function will return true.
   *
   * @actions The actions to check
   * @returns `true` if selection is required, `false` if not.
   */
  actionsRequireSelection(actions: TableAction[]): boolean {
    if (!actions) return false;
    return (
      this.actionsInScope(actions).filter((action) => !action.enabledRules.includes(ActionEnabledRule.Always)).length >
      0
    );
  }

  /**
   * Determines actions that currently have access.
   *
   * @param actions The actions of the table
   * @returns filtered array of actions that do have access.
   */
  actionsInScope(actions: TableAction[]): TableAction[] {
    return actions.filter((action) => this.scope.hasAccesses(action.scope));
  }

  /**
   * Load data for table from dataSource
   */
  loadTable(options: any = {}) {
    this.deselectAllDataRows();
    const tableParams = options.customParams ? options.customParams : this.paginationParams;
    this.dataSource.loadData(tableParams, options);
  }

  /**
   * Adds data to the actionsTriggered event when an action is triggered.
   */
  onActionTriggered(name: string) {
    this.actionTriggered.next(new ActionTrigger<any>(name, this.selection.selected, { totalItems: this.totalItems }));
  }

  /**
   * Gets paginator height and sets it as bottom padding for rav-content.
   */
  adjustLayoutContentPadding() {
    if (this.mobile.isMobileDisplay && !this.config.mobileSettings.preventFixedPaginator) {
      const paginator = document.getElementById('table-paginator');
      const content = document.getElementById('layout-rav-content');
      if (paginator && content) content.style.paddingBottom = `${paginator.offsetHeight}px`;
    }
  }

  /**
   * Triggers a row action that is performed by the user.
   *
   * @param name of the action that the user performed.
   * @param row data on which the action was performed.
   */
  onRowActionTriggered(name: string, row: any): void {
    this.actionTriggered.next(new ActionTrigger<any>(name, [row]));
  }

  /**
   * Clears all rows if at least one is selected. Otherwise, select all if no rows are selected exclude processing rows.
   * You can also pass string 'select all' or 'deselect all' by parameter if you want to force selection or deselection.
   */
  masterToggle(customSelection?: 'select all' | 'deselect all') {
    if (customSelection) {
      if (customSelection === 'deselect all') this.deselectAllDataRows();
      else this.selectAllDataRows();
      return;
    }
    if (this.selection.selected.length > 0) this.deselectAllDataRows();
    else this.selectAllDataRows();
  }

  /**
   * Adds every row to selection.
   */
  selectAllDataRows() {
    this.dataRows.forEach((row) => {
      if (!(row.id && this.processingDictionary[row.id])) {
        this.selection.select(row);
      }
    });
  }

  /**
   * Clears current row selection.
   */
  deselectAllDataRows() {
    this.selection.clear();
    if (this.selectAll) this.selectAll.checked = false;
  }

  onMobileActionsCancelClick() {
    this.deselectAllDataRows();
    this.mobileTable.isSelectingItems = false;
  }

  /**
   * It allows to obtain the current state of the master toggle.
   *
   * @returns master toggle status ('checked', 'unchecked', or 'indeterminate')
   */
  getMasterToggleStatus(): 'checked' | 'unchecked' | 'indeterminate' {
    if (this.selection.hasValue() && this.areAllRowsSelected()) return 'checked';
    if (this.selection.hasValue() && !this.areAllRowsSelected()) return 'indeterminate';
    return 'unchecked';
  }

  /**
   * Retrieves new searches from the table search component and reloads the table
   * if the new searches received are different than the active searches applied.
   *
   * @param newSearches The new active searches sent by the table search component.
   */
  onNewSearch(newSearches: TableSearch[]): void {
    if (this.shouldTableReload(newSearches)) {
      this.activeSearches = newSearches;
      this.paginator.pageIndex = 0;
      this.loadTable();
    }
  }

  /**
   * Search validation is done in the table search component, so we know that any
   * searches sent through newSearches are valid and the table should be reloaded. Also, if there are current
   * activeSearches and newSearches is empty, we need to reload in order to remove the active searches.
   *
   * @param newSearches The new active searches sent by the table search component.
   */
  shouldTableReload(newSearches: TableSearch[]): boolean {
    return this.activeSearches.length > 0 || newSearches.length > 0;
  }

  /**
   * Generates a uri replacing `:id` with the rows ID.
   *
   * @returns the uri with the replaced ID.
   */
  uriWithId(link: string, row: any): string {
    return link.replace(/\:([^\/]+)/g, (match, key) => row[key]);
  }

  /**
   * Launches the view of a row. Can be launched from double-click of a row or the View right-click menu action.
   * rowLaunchLink can either be a string (uri) or callback for mixed document and folder views.
   * Ex. launches the edit document view
   *
   * @param row the row we are launching
   */
  viewRow(row: any) {
    const isInternalLinkValid = row.internal_link !== null;
    if (!this.config.rowLaunchLink || !isInternalLinkValid) return;

    if (typeof this.config.rowLaunchLink === 'string') {
      this.router.navigateByUrl(this.uriWithId(this.config.rowLaunchLink, row));
    } else {
      this.router.navigateByUrl(this.uriWithId(this.config.rowLaunchLink(row), row));
    }
  }

  /**
   * Launches the view of a row in a new browser tab. Can be launched from  the View right-click menu action.
   * rowLaunchLink can either be a string (uri) or callback for mixed document and folder views.
   * Ex. launches the edit document view in a new browser tab
   *
   * @param row the row we are launching
   */
  viewRowInNewTab(row: any) {
    const isInternalLinkValid = row.internal_link !== null;
    if (!this.config.rowLaunchLink || !isInternalLinkValid) return;

    const { rowLaunchLink } = this.config;
    const url =
      typeof rowLaunchLink === 'string' ? this.uriWithId(rowLaunchLink, row) : this.uriWithId(rowLaunchLink(row), row);

    const urlTree = this.router.serializeUrl(this.router.createUrlTree([url]));
    window.open(urlTree, '_blank');
  }

  /**
   * Callback triggered when a checkbox is clicked or a row is clicked. In both of these cases, we toggle the selection
   * state of the row. Also handles multi selection if shift-clicked.
   *
   * @param event the click event
   * @param rowIndex the index of the row being selected
   */
  onRowSelected(event: any, rowIndex: number) {
    // If it is history view then do nothing
    if (this.headerText === 'History') return;

    // If the event target has a route link then do nothing
    if (event && event.target && event.target.attributes && event.target.attributes.href) {
      return;
    }

    event.stopPropagation(); // Prevent the checkbox from actually being "clicked" (we set the value ourselves)

    this.editorService.openOrCloseEditor(null, false, event); // Stop rename editor

    if (event.shiftKey && this.lastRowSelectedIndex.toString()) {
      document.getSelection().removeAllRanges(); // remove text selection done by default
      for (
        let i = Math.min(rowIndex, this.lastRowSelectedIndex);
        i <= Math.max(rowIndex, this.lastRowSelectedIndex);
        i++
      ) {
        this.selection.select(this.dataRows[i]);
      }
    } else this.selection.toggle(this.dataRows[rowIndex]);

    this.changeDetector.detectChanges();
    if (this.selection.isSelected(this.dataRows[rowIndex])) {
      this.lastRowSelectedIndex = rowIndex;
    }
  }

  /**
   * Gets event and row index objects from a single event and calls
   * onRowSelected.
   */
  onMobileRowSelected(event: any) {
    this.onRowSelected(event.event, event.rowIndex);
  }

  /**
   * Sorts the table based on the parameter criteria.
   *
   * @param newTableSort object containing column key and boolean indicating whether it is ascending or not
   */
  updateSort(newTableSort: TableSort) {
    this.sort.sort({
      id: newTableSort.key,
      start: newTableSort.asc ? 'asc' : 'desc',
      disableClear: false
    });
  }

  /**
   * Determines whether all of the rows are currently selected.
   *
   * @returns true if all rows are selected, false if not.
   */
  areAllRowsSelected(): boolean {
    return this.selection.selected.length === this.dataRows.length;
  }

  /**
   * Callback triggered when a row is right-clicked (contextmenu). The row is selected upon click and then the menu has its
   * position set and is opened.
   *
   * @param event the right-click event
   * @param row the row that was right-clicked
   */
  openActionsMenu(event: any, row: any, mobile?: boolean) {
    // If it is history view then do nothing
    if (this.headerText === 'History') return;

    event.stopPropagation(); // Prevent event from firing up to mat-row on mobileActions click
    event.preventDefault(); // Suppress the browser's context menu

    this.editorService.openOrCloseEditor(null, false, null); // Stop rename editor

    // The actions in the new mobile friendly table are for a single file.
    // For multiple file actions use selection mode and banner on top.
    if (mobile) this.deselectAllDataRows();

    this.selection.select(row);
    this.lastRowRightClicked = row;
    this.rightClickMenuPosition.x = event.clientX + 'px';
    this.rightClickMenuPosition.y = event.clientY + 'px';

    if (this.rightClickMenuTrigger) this.rightClickMenuTrigger.openMenu();
    window.addEventListener('scroll', this.closeActionsMenu);
  }

  /**
   * Gets event and row objects from a single event and calls openActionsMenu
   * with mobile = true.
   */
  openMobileActionsMenu(event: any) {
    this.openActionsMenu(event.event, event.row, true);
  }

  /**
   * Closes the action menu and removes the event listener
   */
  closeActionsMenu = () => {
    if (this.rightClickMenuTrigger) {
      this.rightClickMenuTrigger.closeMenu();
      window.removeEventListener('scroll', this.closeActionsMenu);
    }
    // eslint-disable-next-line @typescript-eslint/semi, @typescript-eslint/member-delimiter-style
  };

  /**
   * Callback triggered upon click of a right-click menu action. Either launches the view action
   * or emits the action to the parent component to handle.
   *
   * @param name name of the action clicked
   */
  onRightClickMenuAction(name: string) {
    if (name === 'view') {
      this.viewRow(this.lastRowRightClicked);
      return;
    }
    if (name === 'Open in new tab') {
      this.viewRowInNewTab(this.lastRowRightClicked);
      return;
    }
    if (name === 'Rename') {
      this.editorService.openOrCloseEditor(this.selection.selected[0].id, true, null);
    } else {
      this.actionTriggered.next(new ActionTrigger(name, this.selection.selected, { totalItems: this.totalItems }));
    }
  }

  /**
   * Event handler for when a row is started to drag. Force selects the dragged row and force updates the UI
   * as Angular seems to prevent this from happening automatically as the row is being dragged.
   *
   * @param row The actual row on which the drag event started
   */
  onDrag(row: any) {
    this.dragDrop.isDragging = true;

    // force update row selection on row where drag started (UI doesn't want to update while being dragged otherwise)
    this.selection.select(row);
    document.getElementById('row' + row.id).classList.add('table__row-disabled', 'table__row-selected');
    (document.getElementById('checkbox' + row.id) as any).checked = true;
    this.changeDetector.detectChanges();
  }

  /**
   * Event handler for when the drag/drop selection is dropped. Functionality is broken up
   * in service . If files are successfully moved, reload the table view.
   */
  async onDrop() {
    this.dragDrop.isDragging = false;

    // return if we somehow aren't dragging anything or target wasn't recorded
    if (!this.dragDrop.target || this.selection.selected.length === 0) return;

    try {
      switch (this.dragDrop.target.region) {
        case DragDropRegion.TableRow:
          if (await this.dragDrop.dropOnTableRow(this.selection.selected)) {
            this.dataSource.reloadData.emit();
          }
          break;
        case DragDropRegion.FolderMenuNode:
          if (await this.dragDrop.dropOnFolderMenuNodes(this.selection.selected)) {
            this.dataSource.reloadData.emit();
          }
          break;
        case DragDropRegion.OtherSidebarMenu:
          if (await this.dragDrop.dropOnOtherSidebarMenu(this.selection.selected)) {
            this.dataSource.reloadData.emit();
          }
          break;
      }
    } catch (error) {
      this.dragDrop.dropRejectedSnackbar('Unexpected error trying to move your files - ' + error);
    }
  }

  /**
   * Event handler of drop event. Will get the File object from selected files/folders and start importing jobs.
   *
   * @param ev the drop event.
   * @param row the row that drop event happened.
   */
  async dropHandler(ev, row?: any): Promise<void> {
    if (!this.canDragDropFiles) return;

    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();

    this.isDraggingOver = false;
    let targetFolder;
    const items: DataTransferItemList = ev.dataTransfer.items;
    const allSelectedFile: File[] = [];
    const allSelectedFolders: any[] = [];

    // Filter out folder and documents and process them in different ways.
    // There are import-files component and import -folder component to handle upload differently.
    for (let i = 0; i < items.length; i++) {
      const fileEntry = items[i].webkitGetAsEntry();
      if (fileEntry && fileEntry.isFile) {
        allSelectedFile.push(items[i].getAsFile());
      }
      if (fileEntry && fileEntry.isDirectory) {
        allSelectedFolders.push(fileEntry);
      }
    }

    // Set the drag/drop target folder
    // When the user is dragging over the table row
    let inbox: FolderWithPaginatedDocuments;
    if (row) {
      if (row.type === 'folder') {
        const title = row.title;
        targetFolder = new FolderNode(row.id, title, 0, ',[]');
      }
      if (row.type === 'document') {
        const title = await this.folderReferenceService.getFolderName(row.parent_id);
        targetFolder = new FolderNode(row.parent_id, title, 0, ',[]');
      }
    } else {
      // When the user is dragging over the table header
      let targetFolderId;

      if (this.urlPath[2]) {
        targetFolderId = this.urlPath[2];
      } else {
        if (this.router.url.includes('inbox')) {
          inbox = await this.folderReferenceService.getInbox();
          targetFolderId = inbox.id;
        } else {
          targetFolderId = (await this.folderReferenceService.getTopFolder()).id;
        }
      }
      targetFolder = new FolderNode(targetFolderId, this.headerText, 0, ',[]');
    }

    // Alert user when user try to import folder into Inbox.
    if (inbox === undefined) inbox = await this.folderReferenceService.getInbox();
    const selectedFolderNumber = allSelectedFolders.length;
    if (targetFolder.id === inbox.id && selectedFolderNumber) {
      alert(`Cannot import folder in Inbox`);
      return;
    }

    // Notify the observable in import-files component observable that someone is importing files in table view.
    if (allSelectedFile.length) {
      this.importStatusService.dragImport(new DragImportObject(ImportType.File, allSelectedFile, targetFolder));
    }

    // Iterate each selected folder, and generate traversed files of each folder/directories.
    // Then Notify the observable in import-folders component that someone is importing folder in table view.
    for (let i = 0; i < selectedFolderNumber; i++) {
      const traversedFile = await this.getAllFileEntries(allSelectedFolders[i]);
      const fileResult = await Promise.all(traversedFile.map((fileEntry) => this.getFile(fileEntry)));
      const fullPath = traversedFile.map((file) => file.fullPath);
      this.importStatusService.dragImport(new DragImportObject(ImportType.Folder, fileResult, targetFolder, fullPath));
    }
  }

  /**
   * Handler for dragleave event.
   */
  dragLeaveHandler() {
    this.isDraggingOver = false;
  }

  /**
   * Use BFS to traverse entire directory/file structure
   *
   * @param fileEntry The file entry need to be scanned.
   */
  async getAllFileEntries(fileEntry: any): Promise<any[]> {
    const fileEntries = [];
    const queue = [];
    queue.push(fileEntry);

    while (queue.length > 0) {
      const entry = queue.shift();
      if (entry.isFile) {
        fileEntries.push(entry);
      } else if (entry.isDirectory) {
        queue.push(...(await this.readAllDirectoryEntries(entry)));
      }
    }

    return fileEntries;
  }

  /**
   * Get all the entries (files or sub-directories) in a directory by calling readEntries until it returns empty array
   *
   * @param entry the directory need to get all the entries.
   */
  async readAllDirectoryEntries(entry) {
    const entries = [];
    // create a FileSystemDirectoryReader object which can be used to read the entries in the directory.
    const reader = entry.createReader();
    let readEntries = await this.readEntriesPromise(reader);

    while (readEntries.length > 0) {
      entries.push(...readEntries);
      readEntries = await this.readEntriesPromise(reader);
    }

    return entries;
  }

  /**
   * Retrieves the directory entries within the directory being read and delivers them in an array.
   * Wrap readEntries in a promise to make working with readEntries easier.
   *
   * @param directoryReader a FileSystemDirectoryReader object which can be used to read the entries in the directory.
   */
  async readEntriesPromise(directoryReader): Promise<any> {
    try {
      return await new Promise((resolve, reject) => {
        directoryReader.readEntries(resolve, reject);
      });
    } catch (err) {
      throw err;
    }
  }

  /**
   * Set the class needed depending on the params passed
   *
   * @param key   the column key
   * @param value the value of the column key
   * @param isHeader When this function is in a header cell the flag is true
   */
  getCellClass(key: string, value: string, isHeader: boolean = false): string {
    if (this.headerText === 'History') {
      if (key === 'status' && !isHeader) {
        switch (value.toUpperCase()) {
          case 'SUCCESS':
          case 'SENT':
          case 'DELIVERED':
          case 'OPEN':
            return 'success';
          case 'FAILED':
            return 'danger';
          default:
            return '';
        }
      } else if (key === 'type') return 'table__history-type';
    }
  }

  /**
   * Set isDraggingOver,dragZoneRowId and dragZoneRowTitle to show the drag drop banner when user is dragging over.
   *
   * @param ev drag over event.
   * @param row The dragged over row target.
   */
  dragOverHandler(ev: any, row?: any): void {
    if (!this.canDragDropFiles) return;

    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();

    this.isDraggingOver = true;
    if (row) {
      this.dragZoneRowId = row.id;

      if (row.type === 'folder') {
        this.dragZoneRowTitle = row.title;
      }
      if (row.type === 'document') {
        this.dragZoneRowTitle = this.headerText;
      }
    } else {
      this.dragZoneRowTitle = this.headerText;
    }
  }

  /**
   * Overwrites 'updated_at' and 'version' attribute for a specific row, only locally.
   *
   * @param event onEdit event.
   * @param row The row which name has been changed.
   */
  updateLocalAttributes(event: any, row: any) {
    if (row['updated_at']) row['updated_at'] = event.updated.toISOString();
    if (!isNaN(row['version'])) {
      const versionArray = row['version'].split('.');
      row['version'] = `${versionArray[0]}.${+versionArray[1] + 1}`;
    }
  }

  /**
   * @returns true if row has processing status and processing status is 'error'
   */
  rowHasError(row) {
    return row && row.processing_status === DocumentProcessingStatus.Error;
  }

  private e2eEncryptionStatusCheck(status) {
    for (const doc of this.dataRows) {
      if (status === AccountE2eEncryption.Activating || status === AccountE2eEncryption.Deactivating) {
        this.processingDictionary[doc.id] = true;
      } else if (!doc.processing_status || !(doc.processing_status === 'Pending')) {
        this.processingDictionary[doc.id] = false;
      }
    }
  }

  /**
   * Get the File object which can be used to read data from the file represented by the directory entry.
   *
   * @param fileEntry The file entry.
   */
  private getFile(fileEntry): Promise<File> {
    return new Promise((resolve, reject) => fileEntry.file(resolve, reject));
  }
}
